/**
 * Edit Studio Profile Page
 * Allows studios to manage their hiring preferences, pay rates, and terms
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { ArrowLeft, Building, Camera, DollarSign, FileText, Image, ScrollText, Check } from "lucide-react";

import { 
  RoleSelector,
  LocationInput,
  StudioBasicInfo, 
  StudioHiringPrefs, 
  StudioPaymentTerms,
  PortfolioUsageTerms,
  PortfolioSection,
  VideoPortfolioSection,
  GearBagSection
} from "@/components/profile";
import ContractTemplates from "@/components/profile/ContractTemplates";

export default function EditStudioProfile() {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState("basic");

  // Role state
  const [roleAssistant, setRoleAssistant] = useState(
    user?.roles?.includes("assistant") ?? false
  );
  const [roleStudio, setRoleStudio] = useState(
    user?.roles?.includes("studio") ?? true
  );

  // Location state (shared with assistant profile)
  const [city, setCity] = useState(user?.location?.city || "");
  const [state, setState] = useState(user?.location?.state || "");
  const [zipCode, setZipCode] = useState(user?.location?.zip_code || "");
  const [workRadius, setWorkRadius] = useState(user?.location?.work_radius || 50);

  // Active status
  const [isActive, setIsActive] = useState(user?.studio_profile?.hiring_active !== false);

  // Get existing studio profile
  const existingProfile = user?.studio_profile || {};

  // Studio Basic Info
  const [studioName, setStudioName] = useState(existingProfile.studio_name || user?.name || "");
  const [studioPhone, setStudioPhone] = useState(existingProfile.phone || user?.phone || "");
  const [yearsInBusiness, setYearsInBusiness] = useState(existingProfile.years_in_business || "");
  const [studioLogo, setStudioLogo] = useState(existingProfile.studio_logo || "");
  const [websiteUrl, setWebsiteUrl] = useState(existingProfile.website_url || "");
  const [instagramHandle, setInstagramHandle] = useState(existingProfile.instagram_handle || "");
  const [facebookUrl, setFacebookUrl] = useState(existingProfile.facebook_url || "");
  const [linkedinUrl, setLinkedinUrl] = useState(existingProfile.linkedin_url || "");

  // Hiring Preferences
  const [hiringGenres, setHiringGenres] = useState(existingProfile.hiring_genres || []);
  const [shootingStyles, setShootingStyles] = useState(existingProfile.shooting_styles || []);
  const [editingStyles, setEditingStyles] = useState(existingProfile.editing_styles || []);
  const [lightingStyles, setLightingStyles] = useState(existingProfile.lighting_styles || []);

  // Payment Terms
  const [payRates, setPayRates] = useState(existingProfile.pay_rates || []);
  const [paymentTiming, setPaymentTiming] = useState(existingProfile.payment_timing || "within_30");

  // Portfolio Usage
  const [portfolioUsage, setPortfolioUsage] = useState(existingProfile.portfolio_usage || {
    allowed: "yes",
    after_weeks: null,
    must_credit_studio: false,
    with_studio_watermark: false,
    portfolio_only: false,
    social_media_only: false,
    must_not_tag_client: false,
    additional_terms: ""
  });

  // Shared Portfolio (used by both Studio and Assistant profiles)
  const existingAssistantProfile = user?.assistant_profile || {};
  const [portfolio, setPortfolio] = useState(
    existingProfile.portfolio || existingAssistantProfile.portfolio || []
  );
  
  // Video portfolio
  const [portfolioVideos, setPortfolioVideos] = useState(
    existingProfile.portfolio_videos || existingAssistantProfile.portfolio_videos || []
  );
  
  // Gear items - shared between Studio and Assistant profiles
  const [gearItems, setGearItems] = useState(
    existingProfile.gear_items?.length > 0 ? existingProfile.gear_items : (existingAssistantProfile.gear_items || [])
  );
  const [willingToRent, setWillingToRent] = useState(existingAssistantProfile.willing_to_rent || existingProfile.willing_to_rent || false);

  // Auto-save status
  const [saveStatus, setSaveStatus] = useState(""); // "", "saving", "saved"
  const saveTimeoutRef = useRef(null);
  const lastSaveRef = useRef(null);

  // Auto-save function - saves immediately when called
  const autoSave = useCallback(async (fieldName, fieldData) => {
    // Clear any pending save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setSaveStatus("saving");

    try {
      // Get current location data
      let coordinates = null;
      const currentCity = fieldData.city !== undefined ? fieldData.city : city;
      const currentState = fieldData.state !== undefined ? fieldData.state : state;
      const currentZipCode = fieldData.zipCode !== undefined ? fieldData.zipCode : zipCode;
      
      if (currentCity && currentCity.trim()) {
        try {
          const geoResponse = await fetch(`${API}/geocode?address=${encodeURIComponent(currentCity + " " + currentState + " " + currentZipCode)}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          if (geoResponse.ok) {
            const geoData = await geoResponse.json();
            if (geoData.lat && geoData.lng) {
              coordinates = [geoData.lng, geoData.lat];
            }
          }
        } catch (e) {
          console.log("Geocoding failed");
        }
      }

      // Build location object
      let locationData = null;
      if (currentCity || currentState || currentZipCode) {
        locationData = {
          type: "Point",
          coordinates: coordinates || user?.location?.coordinates || [],
          city: currentCity || "",
          state: currentState || "",
          zip_code: currentZipCode || "",
          work_radius: fieldData.workRadius !== undefined ? fieldData.workRadius : workRadius
        };
      }

      // Build roles array
      const currentRoleAssistant = fieldData.roleAssistant !== undefined ? fieldData.roleAssistant : roleAssistant;
      const currentRoleStudio = fieldData.roleStudio !== undefined ? fieldData.roleStudio : roleStudio;
      const roles = [];
      if (currentRoleAssistant) roles.push("assistant");
      if (currentRoleStudio) roles.push("studio");

      const updateData = {
        roles,
        phone: fieldData.studioPhone !== undefined ? fieldData.studioPhone : studioPhone,
        location: locationData,
        studio_profile: {
          hiring_active: fieldData.isActive !== undefined ? fieldData.isActive : isActive,
          studio_name: fieldData.studioName !== undefined ? fieldData.studioName : studioName,
          years_in_business: fieldData.yearsInBusiness !== undefined ? (fieldData.yearsInBusiness ? parseInt(fieldData.yearsInBusiness) : null) : (yearsInBusiness ? parseInt(yearsInBusiness) : null),
          studio_logo: fieldData.studioLogo !== undefined ? fieldData.studioLogo : studioLogo,
          website_url: fieldData.websiteUrl !== undefined ? fieldData.websiteUrl : websiteUrl,
          instagram_handle: fieldData.instagramHandle !== undefined ? fieldData.instagramHandle : instagramHandle,
          facebook_url: fieldData.facebookUrl !== undefined ? fieldData.facebookUrl : facebookUrl,
          linkedin_url: fieldData.linkedinUrl !== undefined ? fieldData.linkedinUrl : linkedinUrl,
          hiring_genres: fieldData.hiringGenres !== undefined ? fieldData.hiringGenres : hiringGenres,
          shooting_styles: fieldData.shootingStyles !== undefined ? fieldData.shootingStyles : shootingStyles,
          editing_styles: fieldData.editingStyles !== undefined ? fieldData.editingStyles : editingStyles,
          lighting_styles: fieldData.lightingStyles !== undefined ? fieldData.lightingStyles : lightingStyles,
          gear_items: fieldData.gearItems !== undefined ? fieldData.gearItems : gearItems,
          willing_to_rent: fieldData.willingToRent !== undefined ? fieldData.willingToRent : willingToRent,
          pay_rates: fieldData.payRates !== undefined ? fieldData.payRates : payRates,
          payment_timing: fieldData.paymentTiming !== undefined ? fieldData.paymentTiming : paymentTiming,
          portfolio_usage: fieldData.portfolioUsage !== undefined ? fieldData.portfolioUsage : portfolioUsage,
          portfolio: fieldData.portfolio !== undefined ? fieldData.portfolio : portfolio,
          portfolio_videos: fieldData.portfolioVideos !== undefined ? fieldData.portfolioVideos : portfolioVideos,
          team_members_hired: existingProfile.team_members_hired || 0,
          jobs_posted: existingProfile.jobs_posted || 0
        }
      };

      // Also update assistant profile if user has assistant role
      if (currentRoleAssistant && user?.assistant_profile) {
        updateData.assistant_profile = {
          ...user.assistant_profile,
          portfolio: fieldData.portfolio !== undefined ? fieldData.portfolio : portfolio,
          portfolio_videos: fieldData.portfolioVideos !== undefined ? fieldData.portfolioVideos : portfolioVideos,
          gear_items: fieldData.gearItems !== undefined ? fieldData.gearItems : gearItems
        };
      }

      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${API}/users/profile`, {
        method: "PUT",
        headers,
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        updateUser(updatedUser);
        lastSaveRef.current = Date.now();
        setSaveStatus("saved");
        
        // Clear "saved" status after 2 seconds
        saveTimeoutRef.current = setTimeout(() => {
          setSaveStatus("");
        }, 2000);
      } else {
        setSaveStatus("");
        const error = await response.json();
        toast.error(error.detail || "Failed to save");
      }
    } catch (error) {
      console.error("Auto-save error:", error);
      setSaveStatus("");
      toast.error("Failed to save changes");
    }
  }, [token, user, updateUser, city, state, zipCode, workRadius, roleAssistant, roleStudio, isActive, studioName, yearsInBusiness, studioLogo, websiteUrl, instagramHandle, facebookUrl, linkedinUrl, hiringGenres, shootingStyles, editingStyles, lightingStyles, gearItems, payRates, paymentTiming, portfolioUsage, portfolio, portfolioVideos, existingProfile]);

  // Debounced auto-save for text fields (300ms delay)
  const debouncedAutoSave = useCallback((fieldName, fieldData) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setSaveStatus("saving");
    saveTimeoutRef.current = setTimeout(() => {
      autoSave(fieldName, fieldData);
    }, 500);
  }, [autoSave]);

  // Wrapper functions for each field that trigger auto-save
  const handleStudioNameChange = (value) => {
    setStudioName(value);
    debouncedAutoSave("studioName", { studioName: value });
  };

  const handleYearsInBusinessChange = (value) => {
    setYearsInBusiness(value);
    debouncedAutoSave("yearsInBusiness", { yearsInBusiness: value });
  };

  const handleStudioLogoChange = (value) => {
    setStudioLogo(value);
    autoSave("studioLogo", { studioLogo: value });
  };

  const handleWebsiteUrlChange = (value) => {
    setWebsiteUrl(value);
    debouncedAutoSave("websiteUrl", { websiteUrl: value });
  };

  const handleInstagramHandleChange = (value) => {
    setInstagramHandle(value);
    debouncedAutoSave("instagramHandle", { instagramHandle: value });
  };

  const handleFacebookUrlChange = (value) => {
    setFacebookUrl(value);
    debouncedAutoSave("facebookUrl", { facebookUrl: value });
  };

  const handleLinkedinUrlChange = (value) => {
    setLinkedinUrl(value);
    debouncedAutoSave("linkedinUrl", { linkedinUrl: value });
  };

  const handleHiringGenresChange = (value) => {
    setHiringGenres(value);
    autoSave("hiringGenres", { hiringGenres: value });
  };

  const handleShootingStylesChange = (value) => {
    setShootingStyles(value);
    autoSave("shootingStyles", { shootingStyles: value });
  };

  const handleEditingStylesChange = (value) => {
    setEditingStyles(value);
    autoSave("editingStyles", { editingStyles: value });
  };

  const handleLightingStylesChange = (value) => {
    setLightingStyles(value);
    autoSave("lightingStyles", { lightingStyles: value });
  };

  const handleGearItemsChange = (value) => {
    setGearItems(value);
    autoSave("gearItems", { gearItems: value });
  };

  const handlePayRatesChange = (value) => {
    setPayRates(value);
    autoSave("payRates", { payRates: value });
  };

  const handlePaymentTimingChange = (value) => {
    setPaymentTiming(value);
    autoSave("paymentTiming", { paymentTiming: value });
  };

  const handlePortfolioUsageChange = (value) => {
    setPortfolioUsage(value);
    autoSave("portfolioUsage", { portfolioUsage: value });
  };

  const handlePortfolioChange = (value) => {
    setPortfolio(value);
    autoSave("portfolio", { portfolio: value });
  };

  const handlePortfolioVideosChange = (value) => {
    setPortfolioVideos(value);
    autoSave("portfolioVideos", { portfolioVideos: value });
  };

  const handleIsActiveChange = (value) => {
    setIsActive(value);
    autoSave("isActive", { isActive: value });
  };

  const handleRoleAssistantChange = (value) => {
    setRoleAssistant(value);
    autoSave("roleAssistant", { roleAssistant: value });
  };

  const handleRoleStudioChange = (value) => {
    setRoleStudio(value);
    autoSave("roleStudio", { roleStudio: value });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Redirect if not a studio
  useEffect(() => {
    if (user && !user.roles?.includes("studio")) {
      toast.error("You need to enable the 'Hire Talent' role to access this page");
      navigate("/profile/edit");
    }
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Dashboard</Link>
        <Link to="/profile/edit" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
          For Hire Profile
        </Link>
        <NavActions />
      </NavBar>

      {/* Main Content */}
      <main className="flex-1 pt-24 max-w-4xl mx-auto px-6 py-8 w-full">
        {/* Back link and title */}
        <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider">Back to Dashboard</span>
        </Link>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">Edit Studio Profile</h1>
        
        {/* Save Status Indicator */}
        {saveStatus && (
          <div className={`fixed top-20 right-6 z-50 flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${
            saveStatus === "saving" ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"
          }`}>
            {saveStatus === "saving" ? (
              <>
                <div className="w-3 h-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Saved
              </>
            )}
          </div>
        )}

        {/* Active Status Toggle */}
        <div className="flex items-center justify-between p-4 mb-6 bg-white/[0.03] border border-white/10 rounded" data-testid="studio-active-toggle-section">
          <div>
            <p className="font-medium text-sm">Studio Active</p>
            <p className="text-white/40 text-xs mt-0.5">When inactive, your studio won't appear in searches</p>
          </div>
          <button
            type="button"
            onClick={() => handleIsActiveChange(!isActive)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${isActive ? "bg-[#3498DB]" : "bg-white/15"}`}
            data-testid="studio-active-toggle"
          >
            <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${isActive ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-white/[0.03] border border-white/5 p-1 rounded-sm mb-8">
              <TabsTrigger 
                value="basic" 
                className="flex-1 data-[state=active]:bg-white data-[state=active]:text-black rounded-sm"
              >
                <Building className="w-4 h-4 mr-2" />
                Basic
              </TabsTrigger>
              <TabsTrigger 
                value="hiring" 
                className="flex-1 data-[state=active]:bg-white data-[state=active]:text-black rounded-sm"
              >
                <Camera className="w-4 h-4 mr-2" />
                Hiring
              </TabsTrigger>
              <TabsTrigger 
                value="terms" 
                className="flex-1 data-[state=active]:bg-white data-[state=active]:text-black rounded-sm"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Terms
              </TabsTrigger>
              <TabsTrigger 
                value="portfolio-images" 
                className="flex-1 data-[state=active]:bg-white data-[state=active]:text-black rounded-sm"
              >
                <Image className="w-4 h-4 mr-2" />
                Portfolio
              </TabsTrigger>
              <TabsTrigger 
                value="portfolio" 
                className="flex-1 data-[state=active]:bg-white data-[state=active]:text-black rounded-sm"
              >
                <FileText className="w-4 h-4 mr-2" />
                Usage Policy
              </TabsTrigger>
              <TabsTrigger 
                value="contracts" 
                className="flex-1 data-[state=active]:bg-white data-[state=active]:text-black rounded-sm"
              >
                <ScrollText className="w-4 h-4 mr-2" />
                Contracts
              </TabsTrigger>
            </TabsList>

          <form onSubmit={(e) => e.preventDefault()}>
            {/* Basic Tab */}
            <TabsContent value="basic">
              <RoleSelector
                roleAssistant={roleAssistant}
                setRoleAssistant={handleRoleAssistantChange}
                roleStudio={roleStudio}
                setRoleStudio={handleRoleStudioChange}
              />

              <StudioBasicInfo
                studioName={studioName}
                setStudioName={handleStudioNameChange}
                studioPhone={studioPhone}
                setStudioPhone={(v) => { setStudioPhone(v); handleFieldChange({ studioPhone: v }); }}
                yearsInBusiness={yearsInBusiness}
                setYearsInBusiness={handleYearsInBusinessChange}
                studioLogo={studioLogo}
                setStudioLogo={handleStudioLogoChange}
                websiteUrl={websiteUrl}
                setWebsiteUrl={handleWebsiteUrlChange}
                instagramHandle={instagramHandle}
                setInstagramHandle={handleInstagramHandleChange}
                facebookUrl={facebookUrl}
                setFacebookUrl={handleFacebookUrlChange}
                linkedinUrl={linkedinUrl}
                setLinkedinUrl={handleLinkedinUrlChange}
                token={token}
              />

              <LocationInput
                city={city}
                setCity={setCity}
                state={state}
                setState={setState}
                zipCode={zipCode}
                setZipCode={setZipCode}
                workRadius={workRadius}
                setWorkRadius={setWorkRadius}
              />

              <div className="flex justify-end">
                <Button 
                  type="button"
                  onClick={() => setActiveTab("hiring")} 
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                >
                  Next: Hiring Preferences
                </Button>
              </div>
            </TabsContent>

            {/* Hiring Tab */}
            <TabsContent value="hiring">
              <StudioHiringPrefs
                hiringGenres={hiringGenres}
                setHiringGenres={handleHiringGenresChange}
                shootingStyles={shootingStyles}
                setShootingStyles={handleShootingStylesChange}
                editingStyles={editingStyles}
                setEditingStyles={handleEditingStylesChange}
                lightingStyles={lightingStyles}
                setLightingStyles={handleLightingStylesChange}
              />
              
              {/* Studio Gear Section */}
              <div className="mb-6">
                <div className="bg-white/[0.03] border border-white/5 rounded-sm p-6 mb-6">
                  <h2 className="text-lg font-semibold mb-2">Your Studio's Gear</h2>
                  <p className="text-white/50 text-sm mb-4">
                    Adding your camera gear helps us match you with candidates who use similar equipment. 
                    Photographers using the same camera brand often have more compatible image styles.
                  </p>
                  <GearBagSection
                    gearItems={gearItems}
                    setGearItems={handleGearItemsChange}
                    willingToRent={willingToRent}
                    setWillingToRent={(val) => { setWillingToRent(val); autoSave("willingToRent", { willingToRent: val }); }}
                  />
                  <p className="text-white/40 text-sm mt-2">
                    💡 Your gear is shared between your Studio and For-Hire profiles.
                  </p>
                </div>
              </div>

              <div className="flex justify-between">
                <Button 
                  type="button"
                  onClick={() => setActiveTab("basic")} 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                >
                  Back
                </Button>
                <Button 
                  type="button"
                  onClick={() => setActiveTab("terms")} 
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                >
                  Next: Payment Terms
                </Button>
              </div>
            </TabsContent>

            {/* Terms Tab */}
            <TabsContent value="terms">
              <StudioPaymentTerms
                payRates={payRates}
                setPayRates={handlePayRatesChange}
                paymentTiming={paymentTiming}
                setPaymentTiming={handlePaymentTimingChange}
              />

              <div className="flex justify-between">
                <Button 
                  type="button"
                  onClick={() => setActiveTab("hiring")} 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                >
                  Back
                </Button>
                <Button 
                  type="button"
                  onClick={() => setActiveTab("portfolio-images")} 
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                >
                  Next: Portfolio
                </Button>
              </div>
            </TabsContent>

            {/* Portfolio Images Tab */}
            <TabsContent value="portfolio-images">
              <PortfolioSection
                portfolio={portfolio}
                setPortfolio={handlePortfolioChange}
                token={token}
              />
              <p className="text-white/40 text-sm mt-2 mb-6">
                💡 These portfolio images are shared between your Studio and Second Shooter profiles.
              </p>
              
              <div className="mt-10 pt-8 border-t border-white/10">
                <VideoPortfolioSection
                  videos={portfolioVideos}
                  setVideos={handlePortfolioVideosChange}
                />
                <p className="text-white/40 text-sm mt-2">
                  💡 Portfolio videos are also shared between your profiles.
                </p>
              </div>

              <div className="flex justify-between mt-8">
                <Button 
                  type="button"
                  onClick={() => setActiveTab("terms")} 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                >
                  Back
                </Button>
                <Button 
                  type="button"
                  onClick={() => setActiveTab("portfolio")} 
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                >
                  Next: Usage Policy
                </Button>
              </div>
            </TabsContent>

            {/* Portfolio Usage Policy Tab */}
            <TabsContent value="portfolio">
              <PortfolioUsageTerms
                portfolioUsage={portfolioUsage}
                setPortfolioUsage={handlePortfolioUsageChange}
              />

              <div className="flex justify-between">
                <Button 
                  type="button"
                  onClick={() => setActiveTab("portfolio-images")} 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                >
                  Back
                </Button>
                <Button 
                  type="button"
                  onClick={() => navigate(`/profile/${user.user_id}`)}
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                >
                  View Profile
                </Button>
              </div>
            </TabsContent>
          </form>

            {/* Contracts Tab */}
            <TabsContent value="contracts">
              <ContractTemplates />
            </TabsContent>
          </Tabs>
      </main>
      <Footer />
    </div>
  );
}
